import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { api, ApiError } from '@/lib/axios'
import { Toast } from '../components/Toast'

type ActivateApplicationUserResponse = {
  verifyToken: string
  id: string
}

async function activateApplicationUserRequest(id: string, verifyToken: string) {
  const { data } = await api.backend.post<ActivateApplicationUserResponse>(
    `/application-account-users/activate-user/${id}`,
    {
      verifyToken,
    },
  )
  return data
}

export function useActivateApplicationUser() {
  const { t } = useTranslation('common')

  const { mutateAsync, isPending } = useMutation<
    ActivateApplicationUserResponse,
    AxiosError<ApiError>,
    { id: string; verifyToken: string }
  >({
    mutationKey: ['/application-account-users/activate-user'],
    mutationFn: ({ id, verifyToken }) =>
      activateApplicationUserRequest(id, verifyToken),
    onError: (error) => {
      toast(Toast, {
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('app_authentication.errors.activate_user_error'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('app_authentication.errors.activate_user_error'),
        },
      })
    },
  })

  const handleActivateApplicationUser = async (
    id: string,
    verifyToken: string,
  ) => {
    return mutateAsync({ id, verifyToken })
  }

  return { handleActivateApplicationUser, isActivatingUser: isPending }
}
